import React, { useEffect, memo } from "react";
import calculateQuota from "../utils/calculateQuota";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import Icons from "./Icons";
import { Plus, Minus, AlertCircle } from "lucide-react";

/**
 * PlayerList - GCHL Brand styled player data table
 *
 * Uses forest green header, proper dark mode support,
 * and matches the overall admin dashboard styling.
 */
const PlayerList = ({
  allPlayers = [],
  players = [],
  handlePlayerChange,
  toggleEditMode,
  handleDeletePlayer,
  handleSavePlayer,
  selectedEvent,
  handleAddPlayer,
  handleCancelEdit,
  ctpPot,
  skinPot,
  remainingPot,
  isEditAllMode = false,
}) => {
  const isFedupEligible = selectedEvent?.isFedupEligible;
  useEffect(() => {
    tippy("[data-tippy-content]");
  }, [players, selectedEvent]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      tippy("[data-tippy-content]");
    }, 0);
    return () => clearTimeout(timeout);
  }, [players, selectedEvent]);


  return (
    <div className="mt-6">
      {/* Non-FedUp Warning Banner */}
      {!isFedupEligible && (
        <div className="mb-4 p-3 bg-sunset-light/10 dark:bg-sunset-dark/20 border border-sunset-light/30 rounded-xl flex items-center gap-2 text-sunset-dark dark:text-sunset-light">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">
            Non-FedUp Eligible Event - Points will not be counted toward season standings
          </span>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-navy-700 shadow-lg">
        <table className="min-w-full table-fixed border-collapse bg-white dark:bg-navy-900">
          <thead>
            <tr className="bg-forest-900 dark:bg-navy-800 text-white">
              <th className="p-4 text-left w-60 font-semibold">Player</th>
              {isFedupEligible && <th className="p-4 text-center w-20 font-semibold">Place</th>}
              <th className="p-4 text-center w-24 font-semibold">Quota</th>
              <th className="p-4 text-center w-24 font-semibold">Score</th>
              <th className="p-4 text-center w-24 font-semibold">+/-</th>
              <th className="p-4 text-center w-24 font-semibold">New Quota</th>
              {isFedupEligible && <th className="p-3 text-center w-32 font-semibold">CTPs</th>}
              {isFedupEligible && <th className="p-3 text-center w-32 font-semibold">Skins</th>}
              <th className="p-4 text-center w-24 font-semibold">Money</th>
              {isFedupEligible && <th className="p-4 text-center w-32 font-semibold">Points</th>}
              <th className="p-4 text-center w-32 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
            {players.map((player, index) => {

              return (
                <tr
                  key={player.player_id}
                  className={`
                    ${index % 2 === 0
                      ? "bg-white dark:bg-navy-900"
                      : "bg-gray-50 dark:bg-navy-800/50"
                    }
                    hover:bg-forest-50 dark:hover:bg-navy-700/50
                    transition-colors duration-150
                  `}
                >
{/* Player name */}
                  <td className="p-4 text-left w-50">
                    {player.isEditing && !player.player_id ? (
                      <select
                        value={player.player_id || ""}
                        onChange={(e) => {
                          const selectedPlayerId = Number(e.target.value);
                          const selectedPlayer = allPlayers.find(
                            (p) => p.id === selectedPlayerId
                          );
                          handlePlayerChange(selectedPlayerId, {
                            player_id: selectedPlayerId,
                            name: selectedPlayer?.name || "",
                            quota: selectedPlayer?.current_quota || 0,
                            events_played: selectedPlayer?.events_played || 0,
                          });
                        }}
                        className="
                          border border-gray-300 dark:border-navy-600
                          rounded-lg px-2 py-1.5 w-full text-sm
                          bg-white dark:bg-navy-800
                          text-gray-900 dark:text-gray-100
                          focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                          focus:border-forest-500 dark:focus:border-gold-400
                        "
                      >
                        <option value="">Select Player</option>
                        {allPlayers
                          .filter(
                            (p) => !players.some((player) => player.player_id === p.id)
                          )
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{player.name || "-"}</span>
                        {/* Add Badge for Non-FedUp Players */}
                        {!player.season_paid && (
                          <span
                            className="text-sunset-light dark:text-sunset-dark"
                            data-tippy-content="Not a FedUp Cup Participant"
                          >
                            <Icons.XCircle className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                    )}
                  </td>

{/* Rank (Place) */}
{isFedupEligible && (
                  <td className="p-4 text-center w-24">
                    {player.isEditing ? (
                      <select
                        value={player.rank || ""}
                        onChange={(e) => {
                          const newRank = Number(e.target.value);
                          handlePlayerChange(player.player_id, { rank: newRank });
                        }}
                        disabled={!player.player_id}
                        className="
                          border border-gray-300 dark:border-navy-600
                          rounded-lg px-2 py-1.5 w-16 text-sm text-center
                          bg-white dark:bg-navy-800
                          text-gray-900 dark:text-gray-100
                          focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                          disabled:opacity-50 disabled:cursor-not-allowed
                        "
                      >
                        <option value="">--</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((rank) => (
                          <option key={rank} value={rank}>
                            {rank}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={`
                        inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold
                        ${player.rank === 1 ? 'bg-gold-400 text-forest-950' :
                          player.rank === 2 ? 'bg-gray-300 text-gray-800' :
                          player.rank === 3 ? 'bg-amber-600 text-white' :
                          player.rank && player.rank <= 8 ? 'bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-400' :
                          'text-gray-700 dark:text-gray-300'}
                      `}>
                        {player.rank || "-"}
                      </span>
                    )}
                  </td>
                )}
{/* Event Quota */}
                  <td className="p-4 w-24 text-center tabular-nums font-medium text-gray-700 dark:text-gray-300">
                    {player.current_quota || "-"}
                  </td>
{/* Score Editing */}
                  <td className="p-4 w-24 text-center">
                    {player.isEditing ? (
                      <input
                        type="text"
                        value={player.score === 0 || player.score === null ? "" : player.score}
                        placeholder="0"
                        onChange={(e) => {
                          const value = e.target.value;
                          const numericValue = value === "" ? null : Number(value);
                          if (numericValue === null || (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 40)) {
                            handlePlayerChange(player.player_id, { score: numericValue });
                          }
                        }}
                        className="
                          border border-gray-300 dark:border-navy-600
                          rounded-lg w-14 py-1.5 text-center text-sm
                          bg-white dark:bg-navy-800
                          text-gray-900 dark:text-gray-100
                          focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                          transition-all tabular-nums
                        "
                      />
                    ) : (
                      <span className="tabular-nums font-medium text-gray-700 dark:text-gray-300">
                        {player.score || 0}
                      </span>
                    )}
                  </td>
             
{/* +/- */}
                  <td className="p-4 w-24 text-center">
                    {player.score !== null && player.current_quota !== null ? (
                      <>
                        {player.new_player && player.events_played < 2 && player.score - player.current_quota > 2 ? (
                          <div className="flex items-center justify-center gap-1">
                            <span
                              className="text-seafoam-dark font-bold tabular-nums"
                              data-tippy-content={`Score capped at +2 for new player rule. Actual: +${player.score - player.current_quota}. Remaining caps: ${1 - player.events_played}`}
                            >
                              +2
                            </span>
                            <span
                              className="text-forest-600 dark:text-forest-400"
                              title={`Remaining caps: ${1 - player.events_played}`}
                            >
                              <Icons.Shield className="w-3 h-3" />
                            </span>
                          </div>
                        ) : player.score - player.current_quota > 0 ? (
                          <span className="text-seafoam-dark dark:text-seafoam-light font-bold tabular-nums">
                            +{player.score - player.current_quota}
                          </span>
                        ) : player.score - player.current_quota < 0 ? (
                          <span className="text-sunset-light dark:text-sunset-dark font-bold tabular-nums">
                            {player.score - player.current_quota}
                          </span>
                        ) : (
                          <span className="text-gray-500 tabular-nums">0</span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
{/* Calculated Quota */}
                  <td className="p-4 text-center">
                    <span className="font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                      {calculateQuota(player.current_quota, player.score) || 0}
                    </span>
                  </td>

{/* CTPs Editing */}
{isFedupEligible && (
                  <td className="p-3 text-center w-32">
                    {player.isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            handlePlayerChange(player.player_id, { ctps: Math.max(0, (player.ctps || 0) - 1) });
                          }}
                          className="
                            w-7 h-7 rounded-lg flex items-center justify-center
                            bg-gray-100 dark:bg-navy-700
                            text-gray-600 dark:text-gray-300
                            hover:bg-forest-100 hover:text-forest-700
                            dark:hover:bg-forest-900/40 dark:hover:text-forest-400
                            disabled:opacity-40 disabled:cursor-not-allowed
                            transition-colors
                          "
                          disabled={player.ctps <= 0}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-semibold text-gray-900 dark:text-gray-100 w-6 text-center tabular-nums">
                          {player.ctps || 0}
                        </span>
                        <button
                          onClick={() => {
                            handlePlayerChange(player.player_id, { ctps: (player.ctps || 0) + 1 });
                          }}
                          className="
                            w-7 h-7 rounded-lg flex items-center justify-center
                            bg-gray-100 dark:bg-navy-700
                            text-gray-600 dark:text-gray-300
                            hover:bg-forest-100 hover:text-forest-700
                            dark:hover:bg-forest-900/40 dark:hover:text-forest-400
                            disabled:opacity-40 disabled:cursor-not-allowed
                            transition-colors
                          "
                          disabled={player.ctps >= 4}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium text-gray-700 dark:text-gray-300 tabular-nums">
                        {player.ctps || 0}
                      </span>
                    )}
                  </td>
                )}
{/* Skins Editing */}
{isFedupEligible && (
                  <td className="p-3 text-center w-32">
                    {player.isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            handlePlayerChange(player.player_id, { skins: Math.max(0, (player.skins || 0) - 1) });
                          }}
                          className="
                            w-7 h-7 rounded-lg flex items-center justify-center
                            bg-gray-100 dark:bg-navy-700
                            text-gray-600 dark:text-gray-300
                            hover:bg-forest-100 hover:text-forest-700
                            dark:hover:bg-forest-900/40 dark:hover:text-forest-400
                            disabled:opacity-40 disabled:cursor-not-allowed
                            transition-colors
                          "
                          disabled={player.skins <= 0}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-semibold text-gray-900 dark:text-gray-100 w-6 text-center tabular-nums">
                          {player.skins || 0}
                        </span>
                        <button
                          onClick={() => {
                            handlePlayerChange(player.player_id, { skins: (player.skins || 0) + 1 });
                          }}
                          className="
                            w-7 h-7 rounded-lg flex items-center justify-center
                            bg-gray-100 dark:bg-navy-700
                            text-gray-600 dark:text-gray-300
                            hover:bg-forest-100 hover:text-forest-700
                            dark:hover:bg-forest-900/40 dark:hover:text-forest-400
                            transition-colors
                          "
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium text-gray-700 dark:text-gray-300 tabular-nums">
                        {player.skins || 0}
                      </span>
                    )}
                  </td>
                )}
       
{/* Money Editing */}
                  <td className="p-4 w-24 text-center">
                    {player.isEditing ? (
                      <input
                        type="text"
                        value={player.money_won === 0 || player.money_won === null ? "" : player.money_won}
                        placeholder="$0"
                        onChange={(e) => {
                          const value = e.target.value;
                          const numericValue = value === "" ? null : Number(value);
                          if (numericValue === null || (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 1000)) {
                            handlePlayerChange(player.player_id, { money_won: numericValue });
                          }
                        }}
                        className="
                          border border-gray-300 dark:border-navy-600
                          rounded-lg w-16 py-1.5 text-center text-sm
                          bg-white dark:bg-navy-800
                          text-gray-900 dark:text-gray-100
                          focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                          transition-all tabular-nums
                        "
                      />
                    ) : (
                      <span className={`
                        font-semibold tabular-nums
                        ${Number(player.money_won || 0) > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-gray-400 dark:text-gray-600'
                        }
                      `}>
                        ${player.money_won || 0}
                      </span>
                    )}
                  </td>

{/* Total Points */}
{isFedupEligible && (
                  <td className="p-4 text-center w-32">
                    {player.season_paid ? (
                      <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-lg bg-forest-100 dark:bg-forest-900/40 text-forest-700 dark:text-forest-400 font-bold tabular-nums">
                        {Number(player.total_points || 0).toFixed(0)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                )}
{/* Actions */}
                  <td className="p-4 text-center w-32 whitespace-nowrap">
                    {isEditAllMode ? (
                      // In Edit All mode, just show Remove button
                      <button
                        onClick={() => handleDeletePlayer(player.player_id)}
                        className="
                          px-3 py-1.5 rounded-lg text-xs font-semibold
                          bg-sunset-light/10 hover:bg-sunset-light/20
                          dark:bg-sunset-dark/20 dark:hover:bg-sunset-dark/30
                          text-sunset-light dark:text-sunset-dark
                          transition-colors
                        "
                      >
                        Remove
                      </button>
                    ) : player.isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleSavePlayer(player, player.player_id)}
                          className="
                            px-3 py-1.5 rounded-lg text-xs font-semibold
                            bg-forest-600 hover:bg-forest-700
                            dark:bg-gold-400 dark:hover:bg-gold-300
                            text-white dark:text-navy-900
                            transition-colors
                          "
                        >
                          Save
                        </button>
                        <button
                          onClick={() => handleCancelEdit(player.player_id)}
                          className="
                            px-3 py-1.5 rounded-lg text-xs font-semibold
                            bg-gray-200 hover:bg-gray-300
                            dark:bg-navy-700 dark:hover:bg-navy-600
                            text-gray-700 dark:text-gray-300
                            transition-colors
                          "
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => toggleEditMode(player.player_id)}
                          className="
                            px-3 py-1.5 rounded-lg text-xs font-semibold
                            bg-forest-100 hover:bg-forest-200
                            dark:bg-forest-900/40 dark:hover:bg-forest-900/60
                            text-forest-700 dark:text-forest-400
                            transition-colors
                          "
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePlayer(player.player_id)}
                          className="
                            px-3 py-1.5 rounded-lg text-xs font-semibold
                            bg-sunset-light/10 hover:bg-sunset-light/20
                            dark:bg-sunset-dark/20 dark:hover:bg-sunset-dark/30
                            text-sunset-light dark:text-sunset-dark
                            transition-colors
                          "
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}

{selectedEvent && isFedupEligible && (
              <tr className="bg-forest-50 dark:bg-navy-800 border-t-2 border-forest-200 dark:border-navy-600">
                <td colSpan="6" className="p-4 text-right font-semibold text-forest-900 dark:text-forest-400">
                  Remaining Pots:
                </td>
                <td className="p-4 text-center">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                    ${ctpPot.toFixed(2)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                    ${skinPot.toFixed(2)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                    ${remainingPot.toFixed(2)}
                  </span>
                </td>
                <td colSpan="2" className="p-4 text-right">
                  <button
                    onClick={handleAddPlayer}
                    className="
                      inline-flex items-center gap-1.5 px-4 py-2 rounded-lg
                      bg-gold-400 hover:bg-gold-300
                      text-navy-900 font-semibold text-sm
                      transition-colors
                    "
                  >
                    <Plus className="w-4 h-4" />
                    Add Player
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(PlayerList);
